import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, Phone, Calendar, X, Check, Clock, Trash2 } from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d.setDate(diff));
  mon.setHours(0, 0, 0, 0);
  return mon;
}

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isToday(dateStr) {
  return dateStr === formatDate(new Date());
}

export const WeeklyCalendar = ({ apiUrl, token, deals = [] }) => {
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [followups, setFollowups] = useState([]);
  const [showAdd, setShowAdd] = useState(null);
  const [form, setForm] = useState({ deal_name: '', phone: '', followup_type: 'call', scheduled_time: '10:00', notes: '' });

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const weekDates = DAYS.map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const fetchFollowups = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/followups?week_start=${formatDate(weekStart)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setFollowups(data);
      }
    } catch (e) { console.error(e); }
  }, [apiUrl, token, weekStart]);

  useEffect(() => { fetchFollowups(); }, [fetchFollowups]);

  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const goToday = () => setWeekStart(getWeekStart(new Date()));

  const addFollowup = async (dateStr) => {
    if (!form.deal_name.trim()) return;
    try {
      await fetch(`${apiUrl}/api/admin/followups`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...form, scheduled_date: dateStr }),
      });
      setShowAdd(null);
      setForm({ deal_name: '', phone: '', followup_type: 'call', scheduled_time: '10:00', notes: '' });
      fetchFollowups();
    } catch (e) { console.error(e); }
  };

  const toggleComplete = async (fu) => {
    try {
      await fetch(`${apiUrl}/api/admin/followups/${fu.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ completed: !fu.completed }),
      });
      fetchFollowups();
    } catch (e) { console.error(e); }
  };

  const deleteFollowup = async (id) => {
    try {
      await fetch(`${apiUrl}/api/admin/followups/${id}`, { method: 'DELETE', headers });
      fetchFollowups();
    } catch (e) { console.error(e); }
  };

  const getForDate = (dateStr) => followups.filter(f => f.scheduled_date === dateStr)
    .sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || ''));

  const weekLabel = `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const dealSuggestions = deals.map(d => ({ name: d.restaurant_name, phone: d.phone || '' }))
    .filter((d, i, arr) => d.name && arr.findIndex(x => x.name === d.name) === i);

  return (
    <div data-testid="weekly-calendar" style={{ margin: '0', background: '#0f0f0f', borderBottom: '2px solid #222' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.5rem', borderBottom: '2px solid #222' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Calendar size={18} style={{ color: '#FF8C00' }} />
          <h3 style={{ fontFamily: "'Bangers', cursive", fontSize: '1.25rem', color: '#FF8C00', letterSpacing: '0.04em', margin: 0 }}>Follow-Up Calendar</h3>
          <div style={{ padding: '3px 8px', background: '#FF8C0015', border: '1px solid #FF8C00', borderRadius: '4px', color: '#FF8C00', fontSize: '0.75rem', fontWeight: 800 }}>
            {followups.length} this week
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button data-testid="cal-prev-week" onClick={prevWeek} style={{ background: 'none', border: '2px solid #333', borderRadius: '6px', color: '#aaa', cursor: 'pointer', padding: '4px 6px', display: 'flex' }}>
            <ChevronLeft size={16} />
          </button>
          <button data-testid="cal-today" onClick={goToday} style={{ padding: '4px 12px', background: '#FF8C0020', border: '2px solid #FF8C00', borderRadius: '6px', color: '#FF8C00', fontWeight: 800, fontSize: '0.7rem', cursor: 'pointer', textTransform: 'uppercase' }}>Today</button>
          <span style={{ color: '#ccc', fontWeight: 700, fontSize: '0.85rem', minWidth: '160px', textAlign: 'center' }}>{weekLabel}</span>
          <button data-testid="cal-next-week" onClick={nextWeek} style={{ background: 'none', border: '2px solid #333', borderRadius: '6px', color: '#aaa', cursor: 'pointer', padding: '4px 6px', display: 'flex' }}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Week Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', minHeight: '140px' }}>
        {weekDates.map((date, i) => {
          const dateStr = formatDate(date);
          const today = isToday(dateStr);
          const dayFollowups = getForDate(dateStr);
          const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
          const isWeekend = i >= 5;

          return (
            <div
              key={dateStr}
              data-testid={`cal-day-${dateStr}`}
              style={{
                borderRight: i < 6 ? '1px solid #222' : 'none',
                padding: '0.5rem',
                background: today ? '#FF8C0008' : isWeekend ? '#0a0a0a' : 'transparent',
                position: 'relative',
                minHeight: '140px',
              }}
            >
              {/* Day header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: today ? '#FF8C00' : '#666', textTransform: 'uppercase' }}>{DAYS[i]}</span>
                  <span style={{
                    fontSize: '0.85rem', fontWeight: 800,
                    color: today ? '#000' : isPast ? '#555' : '#ccc',
                    background: today ? '#FF8C00' : 'transparent',
                    borderRadius: today ? '50%' : '0',
                    width: today ? '24px' : 'auto', height: today ? '24px' : 'auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{date.getDate()}</span>
                </div>
                <button
                  data-testid={`cal-add-${dateStr}`}
                  onClick={() => { setShowAdd(dateStr); setForm({ deal_name: '', phone: '', followup_type: 'call', scheduled_time: '10:00', notes: '' }); }}
                  style={{ background: 'none', border: '1px solid #333', borderRadius: '4px', color: '#666', cursor: 'pointer', padding: '1px 3px', display: 'flex', lineHeight: 1 }}
                  title="Add follow-up"
                >
                  <Plus size={12} />
                </button>
              </div>

              {/* Follow-ups */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                {dayFollowups.map(fu => (
                  <div
                    key={fu.id}
                    data-testid={`followup-${fu.id}`}
                    style={{
                      padding: '0.3rem 0.4rem',
                      background: fu.completed ? '#22c55e15' : fu.followup_type === 'demo' ? '#6A0DAD20' : '#FF8C0015',
                      border: `1px solid ${fu.completed ? '#22c55e40' : fu.followup_type === 'demo' ? '#6A0DAD50' : '#FF8C0050'}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      opacity: fu.completed ? 0.6 : 1,
                      position: 'relative',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.15rem' }}>
                      <button onClick={() => toggleComplete(fu)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0 }}>
                        {fu.completed
                          ? <Check size={11} color="#22c55e" strokeWidth={3} />
                          : <div style={{ width: '11px', height: '11px', borderRadius: '2px', border: '1.5px solid #555' }} />
                        }
                      </button>
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 700, color: fu.completed ? '#22c55e' : '#ccc',
                        textDecoration: fu.completed ? 'line-through' : 'none',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                      }}>{fu.deal_name}</span>
                      <button onClick={() => deleteFollowup(fu.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0, opacity: 0.4 }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0.4}>
                        <Trash2 size={10} color="#FF3B30" />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      {fu.scheduled_time && (
                        <span style={{ fontSize: '0.6rem', color: '#888', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.15rem' }}>
                          <Clock size={8} /> {fu.scheduled_time}
                        </span>
                      )}
                      <span style={{
                        fontSize: '0.55rem', fontWeight: 800, textTransform: 'uppercase',
                        padding: '1px 4px', borderRadius: '2px',
                        background: fu.followup_type === 'demo' ? '#6A0DAD' : '#FF8C00',
                        color: '#fff',
                      }}>{fu.followup_type}</span>
                    </div>
                    {fu.phone && (
                      <a href={`tel:${fu.phone}`} onClick={e => e.stopPropagation()} style={{ fontSize: '0.6rem', color: '#22c55e', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.15rem', marginTop: '0.1rem' }}>
                        <Phone size={8} /> {fu.phone}
                      </a>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Follow-up Popover */}
              {showAdd === dateStr && (
                <div data-testid={`cal-add-form-${dateStr}`} style={{
                  position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                  zIndex: 50, width: '260px', padding: '1rem',
                  background: '#1a1a1a', border: '2px solid #FF8C00', borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ fontWeight: 800, fontSize: '0.8rem', color: '#FF8C00', textTransform: 'uppercase' }}>
                      {new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <button onClick={() => setShowAdd(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', display: 'flex' }}><X size={14} /></button>
                  </div>

                  <input
                    data-testid="followup-name-input"
                    list="deal-suggestions"
                    value={form.deal_name}
                    onChange={e => {
                      const name = e.target.value;
                      setForm(f => ({ ...f, deal_name: name }));
                      const match = dealSuggestions.find(d => d.name === name);
                      if (match) setForm(f => ({ ...f, deal_name: name, phone: match.phone }));
                    }}
                    placeholder="Restaurant / Contact name"
                    style={{ width: '100%', padding: '6px 8px', background: '#111', border: '2px solid #333', borderRadius: '6px', color: '#fff', fontSize: '0.8rem', fontWeight: 600, outline: 'none', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                  />
                  <datalist id="deal-suggestions">
                    {dealSuggestions.map(d => <option key={d.name} value={d.name} />)}
                  </datalist>

                  <input
                    data-testid="followup-phone-input"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="Phone (optional)"
                    style={{ width: '100%', padding: '6px 8px', background: '#111', border: '2px solid #333', borderRadius: '6px', color: '#fff', fontSize: '0.8rem', fontWeight: 600, outline: 'none', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                  />

                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    {['call', 'demo'].map(t => (
                      <button
                        key={t}
                        data-testid={`followup-type-${t}`}
                        onClick={() => setForm(f => ({ ...f, followup_type: t }))}
                        style={{
                          flex: 1, padding: '5px 8px', borderRadius: '6px', cursor: 'pointer',
                          fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase',
                          background: form.followup_type === t ? (t === 'demo' ? '#6A0DAD' : '#FF8C00') : '#222',
                          color: form.followup_type === t ? '#fff' : '#888',
                          border: `2px solid ${form.followup_type === t ? (t === 'demo' ? '#6A0DAD' : '#FF8C00') : '#444'}`,
                        }}
                      >{t === 'call' ? 'Follow-Up Call' : 'Demo'}</button>
                    ))}
                  </div>

                  <input
                    data-testid="followup-time-input"
                    type="time"
                    value={form.scheduled_time}
                    onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))}
                    style={{ width: '100%', padding: '6px 8px', background: '#111', border: '2px solid #333', borderRadius: '6px', color: '#fff', fontSize: '0.8rem', fontWeight: 600, outline: 'none', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                  />

                  <input
                    data-testid="followup-notes-input"
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Notes (optional)"
                    style={{ width: '100%', padding: '6px 8px', background: '#111', border: '2px solid #333', borderRadius: '6px', color: '#fff', fontSize: '0.8rem', fontWeight: 600, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' }}
                  />

                  <button
                    data-testid="followup-save-btn"
                    onClick={() => addFollowup(dateStr)}
                    style={{
                      width: '100%', padding: '8px', background: '#FF8C00', color: '#000',
                      border: '2px solid #000', borderRadius: '6px', fontWeight: 800, fontSize: '0.8rem',
                      cursor: 'pointer', textTransform: 'uppercase',
                    }}
                  >Schedule</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
